<script>
    /**
     * This Svelte component is adapted from https://npm.im/svelte-typeahead
     * version 4.3.2 (https://github.com/metonym/svelte-typeahead), which was
     * provided with the following license:
     * @license MIT
     */

    /**
     * @template TItem = string | number | Record<string, any>
     */

    import SearchIcon from "@propublica/pp-v5-shared/src/icons/search.svg";

    export let id = "typeahead-" + Math.random().toString(36);
    export let value = "";

    /** @type {TItem[]} */
    export let data = [];

    /** @type {(item: TItem) => any} */
    export let extract = (item) => item;

    /** @type {TItem[]} */
    export let equivalencies = [];

    /** @type {(item: TItem) => boolean} */
    export let filter = (item) => false;

    /** Set to `false` to prevent the first result from being selected */
    export let autoselect = true;

    /**
     * Set to `keep` to keep the search field unchanged after select, set to `clear` to auto-clear search field
     * @type {"update" | "clear" | "keep"}
     */
    export let inputAfterSelect = "update";

    /** @type {{ original: TItem; index: number; string: string }[]} */
    export let results = [];

    /** Set to `true` to only show results when the input is focused */
    export let showDropdownOnFocus = true;

    /**
     * Specify the maximum number of results to return
     * @type {number}
     */
    export let limit = Infinity;

    import search from "../scripts/searchText.js";
    import Search from "svelte-search";
    import { tick, createEventDispatcher, afterUpdate } from "svelte";

    const dispatch = createEventDispatcher();
    const PATH_PREFIX =
        process.env.BAKER_FOR_CMS === "1"
            ? // When building component for CMS usage, use a full URL like
              // `https://projects.propublica.org/repatriation/...`
              `${process.env.DOMAIN}${process.env.PATH_PREFIX}`
            : // When building for app itself, root-relative URL is fine (and probably
              // less prone to bugs): `/repatriation/...`
              process.env.PATH_PREFIX;

    let comboboxRef = null;
    let searchRef = null;
    let hideDropdown = false;
    let selectedIndex = -1;
    let prevResults = "";
    let isFocused = false;

    afterUpdate(() => {
        if (prevResults !== resultsId && autoselect) {
            selectedIndex = getNextIndex();
        }

        if (prevResults !== resultsId && !$$slots["no-results"]) {
            hideDropdown = results.length === 0;
        }

        prevResults = resultsId;
    });

    async function select() {
        const result = results[selectedIndex];
        if (!result) {
            let url = PATH_PREFIX + `search/?query=${encodeURI(value)}`;
            window.location.href = url;
            return;
        }

        const selectedValue = extract(result.original);
        const searchedValue = value;

        if (inputAfterSelect == "clear") value = "";
        if (inputAfterSelect == "update") value = selectedValue;

        dispatch("select", {
            selectedIndex,
            searched: searchedValue,
            selected: selectedValue,
            original: result.original,
            originalIndex: result.index,
        });

        await tick();

        // if (focusAfterSelect) searchRef.focus();
        close();
    }

    /** @type {() => number} */
    function getNextIndex() {
        let index = 0;

        if (index === results.length) {
            index = 0;
        } else {
            index += 1;
        }

        return index;
    }

    /** @type {(direction: -1 | 1) => void} */
    function change(direction) {
        let index = selectedIndex + direction;
        if (index > results.length) {
            index = 0;
        } else if (index < 0) {
            index = results.length;
        }

        selectedIndex = index;
    }

    const open = () => (hideDropdown = false);
    const close = () => (hideDropdown = true);

    $: options = { extract, equivalencies };
    $: results = search
        .filter(value, data, options)
        .slice(0, limit)
        .filter((result) => !filter(result.original));
    $: resultsId = results.map((result) => extract(result.original)).join("");
    $: showResults = !hideDropdown && results.length > 0 && value !== "";
    $: if (showDropdownOnFocus) {
        showResults = showResults && isFocused;
    }
</script>

<svelte:window
    on:click={({ target }) => {
        if (!hideDropdown && !comboboxRef?.contains(target)) {
            close();
        }
    }}
/>
<div
    data-svelte-typeahead
    bind:this={comboboxRef}
    role="combobox"
    aria-haspopup="listbox"
    aria-controls="{id}-listbox"
    class:dropdown={results.length > 0}
    aria-expanded={showResults}
    id="{id}-typeahead"
>
    <Search
        {id}
        removeFormAriaAttributes={true}
        {...$$restProps}
        bind:ref={searchRef}
        enterkeyhint="search"
        aria-autocomplete="list"
        aria-controls="{id}-listbox"
        aria-labelledby="{id}-label"
        aria-activedescendant={selectedIndex >= 0 &&
        !hideDropdown &&
        results.length > 0
            ? `${id}-result-${selectedIndex}`
            : null}
        bind:value
        on:type
        on:input
        on:change
        on:focus
        on:focus={() => {
            open();
            if (showDropdownOnFocus) {
                showResults = true;
                isFocused = true;
            }
        }}
        on:clear
        on:clear={open}
        on:blur
        on:keydown
        on:keydown={(e) => {
            if (results.length === 0) return;

            switch (e.key) {
                case "Enter":
                    select();
                    break;
                case "ArrowDown":
                    e.preventDefault();
                    change(1);
                    break;
                case "ArrowUp":
                    e.preventDefault();
                    change(-1);
                    break;
                case "Escape":
                    e.preventDefault();
                    value = "";
                    searchRef?.focus();
                    close();
                    break;
            }
        }}
    />
    <ul
        class:svelte-typeahead-list={true}
        role="listbox"
        aria-labelledby="{id}-label"
        id="{id}-listbox"
        on:mouseleave={() => {
            selectedIndex = -1;
        }}
    >
        {#if showResults}
            {#if value !== ""}
                {#each results as result, index}
                    <li
                        role="option"
                        id="{id}-result-{index}"
                        class:selected={selectedIndex === index}
                        aria-selected={selectedIndex === index}
                        on:click={() => {
                            selectedIndex = index;
                            select();
                        }}
                        on:mouseenter={() => {
                            selectedIndex = index;
                        }}
                    >
                        <slot {result} {index} {value}>
                            {@html result.string}
                        </slot>
                    </li>
                {/each}
                <li
                    role="option"
                    id="all-result-{limit}"
                    class:selected={selectedIndex === limit}
                    aria-selected={selectedIndex === limit}
                    on:click={() => {
                        selectedIndex = limit;
                        select();
                    }}
                    on:mouseenter={() => {
                        selectedIndex = limit;
                    }}
                >
                    <div class="search-icon">
                        <SearchIcon />
                    </div>
                    See all results for "{value}"
                </li>
            {/if}
        {/if}
        {#if $$slots["no-results"] && !hideDropdown && value.length > 0 && results.length === 0}
            <div class:no-results={true}>
                <slot name="no-results" {value} />
            </div>
        {/if}
    </ul>
</div>

<style lang="scss">
    @import "@propublica/pp-v5-shared/src/css/utils/dark-mode.mixins";

    [data-svelte-typeahead] {
        font-family: var(--fonts-sans);
        position: relative;
        background-color: var(--color-bg);
    }

    ul {
        position: absolute;
        top: 100%;
        left: 0;
        width: 100%;
        padding: 0;
        list-style: none;
        background-color: inherit;
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        margin-top: 2px;
        margin-left: 0;
        z-index: 5;
    }

    [aria-expanded="true"] ul {
        z-index: 1;
    }

    ul.svelte-typeahead-list {
        margin-top: 0;
    }

    li,
    .no-results {
        padding: 4px var(--spacing-1);
    }

    li {
        cursor: pointer;
    }

    li:not(:last-of-type) {
        border-bottom: 1px solid;
        @include set-light-dark(border-color, #e0e0e0, var(--gray-80));
    }

    li:hover {
        @include set-light-dark(background-color, #e5e5e5, var(--gray-80));
    }

    .selected {
        @include set-light-dark(background-color, #e5e5e5, var(--gray-80));
    }

    .selected:hover {
        @include set-light-dark(background-color, #cacaca, var(--gray-70));
    }

    :global([data-svelte-search] label) {
        margin-bottom: 0.25rem;
        display: inline-flex;
        font-size: 0.875rem;
    }

    :global([data-svelte-search] input) {
        width: 100%;
        background: none;
        font-size: 1rem;
        border: 0;
        outline: 1px solid;
        @include set-light-dark(outline-color, var(--gray-10), var(--gray-70));
    }

    :global([data-svelte-search] input:focus) {
        outline-color: #0f62fe;
        outline-offset: 2px;
    }

    div.search-icon {
        width: 20px;
        display: inline-block;
        position: relative;
        top: 4px;
    }

    .svelte-typeahead-list {
        font-size: var(--scale0);
    }
</style>
